'use client';

import { Environment } from '@react-three/drei';
import { useEffect, useState } from 'react';
import type * as THREE from 'three';
import {
  SCENE_ASSET_REPLACED,
  type SceneAssetReplacedDetail,
} from './asset-url-invalidation';
import { loadEnvMap, getCachedEnvMap, invalidateEnvMapCache } from './env-map-loader';
import { resolveEditorHdrUrl } from './hdr-proxy';

function envUrlLooksExr(url: string): boolean {
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  return path.endsWith('.exr');
}

function urlPathMatches(a: string, b: string): boolean {
  return a.split('?')[0].split('#')[0] === b.split('?')[0].split('#')[0];
}

/**
 * Load scene HDR/EXR through loadEnvMap (fetch + parse) instead of drei's
 * useLoader(DataTextureLoader) path, which crashes when parse returns null.
 */
export function ResolvedSceneEnvironment({
  hdrUrl,
  background = false,
  blur = 0,
  environmentIntensity = 1,
  preferHdrProxy = false,
}: {
  hdrUrl: string;
  background?: boolean;
  blur?: number;
  environmentIntensity?: number;
  /** Editor: prefer a `.1k.hdr` sibling when present. Ignored for EXR. */
  preferHdrProxy?: boolean;
}) {
  const [map, setMap] = useState<THREE.Texture | null>(() => getCachedEnvMap(hdrUrl) ?? null);
  const [reloadGen, setReloadGen] = useState(0);

  useEffect(() => {
    const onReplaced = (event: Event) => {
      const { oldUrl, newUrl } = (event as CustomEvent<SceneAssetReplacedDetail>).detail;
      if (!urlPathMatches(hdrUrl, oldUrl) && !urlPathMatches(hdrUrl, newUrl)) return;
      invalidateEnvMapCache(oldUrl);
      if (newUrl) invalidateEnvMapCache(newUrl);
      setReloadGen((g) => g + 1);
    };
    document.addEventListener(SCENE_ASSET_REPLACED, onReplaced);
    return () => document.removeEventListener(SCENE_ASSET_REPLACED, onReplaced);
  }, [hdrUrl]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let url = hdrUrl;
      if (preferHdrProxy && !envUrlLooksExr(hdrUrl)) {
        url = (await resolveEditorHdrUrl(hdrUrl)) ?? hdrUrl;
      }

      if (!cancelled) setMap(null);
      const tex = await loadEnvMap(url);
      if (!cancelled) setMap(tex);
    })().catch(() => {
      if (!cancelled) setMap(null);
    });

    return () => {
      cancelled = true;
    };
  }, [hdrUrl, preferHdrProxy, reloadGen]);

  if (!map) return null;

  return (
    <Environment
      map={map}
      background={background}
      blur={blur}
      environmentIntensity={environmentIntensity}
    />
  );
}
